// ============================================================================
// KI-Assistent — schwebendes Chatfenster
// ============================================================================
// Ein Bauteil fuer alle vier Zugaenge. Welcher es ist, entscheidet der Server
// anhand des mitgeschickten Tokens — hier wird nur der passende Eingang
// gewaehlt. Das Bauteil haelt bewusst keine Rolle im State: waere die Rolle
// clientseitig, koennte man sie im Browser umstellen.
// ============================================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import './KiAssistent.css';

const SPEICHER_SCHLUESSEL = 'tda-ki-verlauf';
const MAX_GESPEICHERT = 20;

function eingangWaehlen() {
  const athleteToken = localStorage.getItem('athleteToken');
  if (athleteToken) {
    return { pfad: '/api/ki-assistent/athlet/frage', token: athleteToken, zugang: 'athlet' };
  }
  const token = localStorage.getItem('token');
  if (token) {
    return { pfad: '/api/ki-assistent/frage', token, zugang: 'angemeldet' };
  }
  return { pfad: '/api/ki-assistent/public/frage', token: null, zugang: 'public' };
}

const STARTHILFE = {
  public: [
    'Wann ist das naechste Turnier?',
    'Wie laeuft der Turniertag ab?',
    'Was kostet ein Start?'
  ],
  angemeldet: [
    'Welche Turniere stehen an?',
    'In welcher Klasse startet mein Kind?',
    'Was ist bei uns schon gemeldet?'
  ],
  athlet: [
    'Wo bin ich angemeldet?',
    'Welche Startklassen passen zu mir?',
    'Wann muss ich auf die Flaeche?'
  ]
};

export default function KiAssistent() {
  const [offen, setOffen] = useState(false);
  const [verfuegbar, setVerfuegbar] = useState(null);
  const [assistent, setAssistent] = useState('Kenji');
  const [nachrichten, setNachrichten] = useState([]);
  const [eingabe, setEingabe] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const endeRef = useRef(null);
  const feldRef = useRef(null);

  const eingang = eingangWaehlen();

  // Gibt es den Assistenten auf diesem Server ueberhaupt? Ohne Schluessel
  // blenden wir den Knopf ganz aus, statt einen toten Chat anzubieten.
  useEffect(() => {
    let abgebrochen = false;
    fetch('/api/ki-assistent/status')
      .then(r => r.json())
      .then(d => {
        if (abgebrochen) return;
        setVerfuegbar(!!d?.data?.verfuegbar);
        if (d?.data?.assistent) setAssistent(d.data.assistent);
      })
      .catch(() => { if (!abgebrochen) setVerfuegbar(false); });
    return () => { abgebrochen = true; };
  }, []);

  // Verlauf ueberlebt einen Seitenwechsel, aber nicht den Browser.
  useEffect(() => {
    try {
      const roh = sessionStorage.getItem(SPEICHER_SCHLUESSEL);
      if (roh) setNachrichten(JSON.parse(roh).slice(-MAX_GESPEICHERT));
    } catch { /* kaputter Eintrag: einfach leer starten */ }
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(SPEICHER_SCHLUESSEL, JSON.stringify(nachrichten.slice(-MAX_GESPEICHERT)));
    } catch { /* Speicher voll oder gesperrt: nicht schlimm */ }
  }, [nachrichten]);

  useEffect(() => {
    if (offen) endeRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [nachrichten, offen, laeuft]);

  useEffect(() => {
    if (offen) feldRef.current?.focus();
  }, [offen]);

  const senden = useCallback(async (text) => {
    const frage = (text ?? eingabe).trim();
    if (!frage || laeuft) return;

    setEingabe('');
    setLaeuft(true);
    const bisher = [...nachrichten, { rolle: 'nutzer', text: frage }];
    setNachrichten(bisher);

    try {
      const kopf = { 'Content-Type': 'application/json' };
      if (eingang.token) kopf.Authorization = `Bearer ${eingang.token}`;

      const antwort = await fetch(eingang.pfad, {
        method: 'POST',
        headers: kopf,
        body: JSON.stringify({ frage, verlauf: nachrichten.slice(-10) })
      });
      const daten = await antwort.json();

      if (!antwort.ok || !daten.success) {
        setNachrichten([...bisher, {
          rolle: 'assistent',
          text: daten?.error || 'Der Assistent konnte gerade nicht antworten.',
          stoerung: true
        }]);
      } else {
        setNachrichten([...bisher, { rolle: 'assistent', text: daten.data.antwort }]);
      }
    } catch {
      setNachrichten([...bisher, {
        rolle: 'assistent',
        text: 'Keine Verbindung zum Assistenten. Bitte spaeter erneut versuchen.',
        stoerung: true
      }]);
    } finally {
      setLaeuft(false);
    }
  }, [eingabe, laeuft, nachrichten, eingang.pfad, eingang.token]);

  const beiTaste = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      senden();
    }
  };

  if (verfuegbar !== true) return null;

  return (
    <>
      {!offen && (
        <button
          className="kia-knopf"
          onClick={() => setOffen(true)}
          aria-label={`${assistent}, den KI-Assistenten, oeffnen`}
        >
          <span className="kia-knopf-icon" aria-hidden="true">🥋</span>
          <span className="kia-knopf-text">Fragen?</span>
        </button>
      )}

      {offen && (
        <div className="kia-fenster" role="dialog" aria-label={`KI-Assistent ${assistent}`}>
          <header className="kia-kopf">
            <div className="kia-kopf-text">
              <strong>{assistent}</strong>
              <span>KI-Assistent · ein Mensch übernimmt bei Bedarf</span>
            </div>
            <div className="kia-kopf-knoepfe">
              {nachrichten.length > 0 && (
                <button
                  className="kia-kopf-knopf"
                  onClick={() => setNachrichten([])}
                  title="Gespräch zurücksetzen"
                >↺</button>
              )}
              <button
                className="kia-kopf-knopf"
                onClick={() => setOffen(false)}
                aria-label="Schließen"
              >✕</button>
            </div>
          </header>

          <div className="kia-verlauf">
            {nachrichten.length === 0 && (
              <div className="kia-start">
                <p>
                  Hallo! Ich bin {assistent} und beantworte Fragen rund um unsere Turniere.
                  Ich bin eine KI, meine Angaben sind unverbindlich.
                </p>
                <div className="kia-vorschlaege">
                  {(STARTHILFE[eingang.zugang] || STARTHILFE.public).map((v) => (
                    <button key={v} className="kia-vorschlag" onClick={() => senden(v)}>{v}</button>
                  ))}
                </div>
              </div>
            )}

            {nachrichten.map((n, i) => (
              <div
                key={i}
                className={`kia-blase kia-blase--${n.rolle}${n.stoerung ? ' kia-blase--stoerung' : ''}`}
              >
                {n.text.split('\n').map((zeile, z) => (
                  <p key={z}>{zeile || ' '}</p>
                ))}
              </div>
            ))}

            {laeuft && (
              <div className="kia-blase kia-blase--assistent kia-tippt">
                <span /><span /><span />
              </div>
            )}
            <div ref={endeRef} />
          </div>

          <div className="kia-eingabe">
            <textarea
              ref={feldRef}
              value={eingabe}
              onChange={(e) => setEingabe(e.target.value)}
              onKeyDown={beiTaste}
              placeholder="Frage eingeben…"
              rows={1}
              maxLength={2000}
              disabled={laeuft}
            />
            <button
              className="kia-senden"
              onClick={() => senden()}
              disabled={laeuft || !eingabe.trim()}
              aria-label="Senden"
            >➤</button>
          </div>
        </div>
      )}
    </>
  );
}
